export enum SupportedComponents {
  blockImagesText = 'BlockImagesText',
  advancedTabsItem = 'AdvancedTabsItem',
  blockEnumeration = 'BlockEnumeration',
  itemNumber = 'ItemNumber',
  blockBlockList = 'BlockBlockList',
  blockTitle = 'BlockTitle',
  itemBlockSimpleDescription = 'ItemBlockSimpleDescription',
  blockTextList = 'BlockTextList',
  blockDoubleTextList = 'BlockDoubleTextList',
  blockExpandableTab = 'BlockExpandableTab',
  blockCardList = 'BlockCardList',
  blockTabsContainer = 'BlockTabsContainer',
  blockTeaserText = 'BlockTeaserText',
  blockEquivalence = 'BlockEquivalence',
  blockDosage = 'BlockDosage',
  itemTabDosage = 'ItemTabDosage',
  blockStackableTabs = 'BlockStackableTabs',
  itemStackableTabs = 'ItemStackableTabs',
  blockRankList3Levels = 'BlockRankList3Levels',
  blockPercentageList = 'BlockPercentageList',
  blockExpandableLink = 'BlockExpandableLink',
  blockTwoSidedCardList = 'BlockTwoSidedCardList',
  itemTwoSidedCard = 'ItemTwoSidedCard',
  itemRank5Levels = 'ItemRank5Levels',
  itemImageText = 'ItemImageText',
  itemTwoTextImageList = 'ItemTwoTextImageList',
  blockFullText = 'BlockFullText',
  blockRichTextList = 'BlockRichTextList',
  blockImageList = 'BlockImageList',
  blockDownloadableFiles = 'BlockDownloadableFiles',
  itemTwoTextImage = 'ItemTwoTextImage',
  itemStage = 'ItemStage',
  blockIngredients = 'BlockIngredients',
  contentRecipeIngredient = 'ContentRecipeIngredient',
  contentAmountForRecipe = 'ContentAmountForRecipe',
  blockIconLabeledLinks = 'BlockIconLabeledLinks',
  itemInternalLink = 'LabeledLink',
  itemExternalLink = 'LabeledLink',
  blockTwoColumnsImageText = 'BlockTwoColumnsImageText',
  blockRecipePreparationSteps = 'BlockRecipePreparationSteps',
  itemRecipePreparationStep = 'ItemRecipePreparationStep',
  contentRecipeEquipment = 'ContentRecipeEquipment',
  blockImage = 'BlockImage',
  blockCategorizedTabContainer = 'BlockCategorizedTabContainer',
  itemCategorizedTab = 'ItemCategorizedTab',
  blockSimpleTabContainer = 'BlockSimpleTabContainer',
  contentSimpleTab = 'ContentSimpleTab',
  blockTwoColumnsTextList = 'BlockTwoColumnsTextList',
  blockImageTextList = 'BlockImageTextList',
  blockRecipeCategories = 'BlockRecipeCategories',
  blockTabsWithBanner = 'BlockTabsWithBanner',
  blockRecipeList = 'BlockRecipeList',
  blockAdviceList = 'BlockAdviceList',
  blockSeoMainTitle = 'BlockSeoMainTitle',
  blockAdviceTabsWithBanner = 'BlockAdviceTabsWithBanner',
  blockSeoListingPage = 'BlockSeoListingPage',
  blockRatingsAndReviews = 'BlockRatingsAndReviews',
  blockOurEngagements = 'BlockOurEngagements',
  blockSocialPosts = 'BlockSocialPosts',
  blockContactForm = 'BlockContactForm',
  itemLinkAxeptioModal = 'ItemLinkAxeptioModal',
}

/**
 * Utility function to check if a string is empty
 * @param str
 * @returns true is empty
 */
function isEmptyString(str: string) {
  return !str || str.trim() === '';
}

/**
 * Utility function to check if a block content is empty, will crawl subcontent recursively by calling checkContentEmpty methods
 * @param items
 * @returns true if empty
 */
function isContentEmpty(items: any[]) {
  if (!items || items.length === 0) return true;

  for (const item of items) {
    const componentName = item?.sys?.contentType?.sys?.id;
    if (!SupportedComponents[componentName]) continue;

    if (item?.fields && typeof checkContentEmpty[componentName] === 'function') {
      let isEmpty = checkContentEmpty[componentName](item.fields);
      if (!isEmpty) {
        return false;
      }
    } else {
      return false;
    }
  }
  return true;
}

/**
 * For each type of component, if the associated method returns true, the component will be filtered
 * While translating, some parts are entire components may remain untranslated, this allows to filter them
 * It is possible to define a checker for each supported component.
 * If no checker is defined, content is supposed to be filled (not empty)
 */
export const checkContentEmpty = {
  blockFullText: (item) => {
    return isEmptyString(item.title) && isEmptyString(item.content);
  },
  blockImagesText: (item) => {
    return isEmptyString(item.title) && !item.text?.content?.length;
  },
  blockBlockList: (item) => {
    return isEmptyString(item.title) && isContentEmpty(item.contents);
  },
  blockImageTextList: (item) => {
    return (
      isEmptyString(item.title) &&
      !item.items?.find(
        ({ fields }) => fields && (fields.image?.fields?.file?.url || fields.title || fields.text?.content?.length),
      )
    );
  },
  itemImageText: (item) => {
    return !item.title && !item.text;
  },
  blockExpandableTab: (item) => {
    return isEmptyString(item.title) && !item.image?.fields?.file?.url && isContentEmpty(item.contents);
  },
  blockCardList: (item) => {
    return isContentEmpty(item.cards);
  },
  itemTwoTextImageList: (item) => {
    return isContentEmpty(item.items);
  },
  itemTwoTextImage: (item) => {
    return !item.image?.fields?.file?.url && isEmptyString(item.firstText) && isEmptyString(item.secondText);
  },
  blockExpandableLink: (item) => {
    return isContentEmpty(item.content);
  },
  blockImage: (item) => {
    return !item.image?.fields?.file?.url;
  },
  blockEnumeration: (item) => {
    return isContentEmpty(item.items);
  },
  itemNumber: (item) => {
    return !item.description?.content?.length;
  },
};
